import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { trackView } from "@/lib/trackView";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, CheckCircle2, Mail, Send } from "lucide-react";

export default function EventDetail() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(false);
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const [rsvp, setRsvp] = useState({ children_count: 0, adults_count: 0, children: [], note: "" });
  const [whosComing, setWhosComing] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactSending, setContactSending] = useState(false);

  const load = () => {
    trackView("event", eventId);
    api.get(`/events/${eventId}`)
      .then((r) => setEvent(r.data))
      .catch(() => setError(true));
    if (user) {
      api.get(`/events/${eventId}/whos-coming`)
        .then((r) => setWhosComing(r.data))
        .catch(() => {});
    }
  };
  useEffect(load, [eventId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="event-not-found">
        <h1 className="text-2xl font-black text-forest mb-2">Event not found</h1>
        <Link to="/events" className="text-terracotta-dark font-bold hover:underline">Back to events</Link>
      </div>
    );
  }
  if (!event) return <div className="text-center py-20 text-forest/50">Loading event...</div>;

  const mine = !!event.my_rsvp;
  const full = event.capacity != null && event.spots_filled >= event.capacity;
  const dateLabel = new Date(event.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const submitRsvp = async () => {
    try {
      await api.post(`/events/${event.event_id}/rsvp`, rsvp);
      toast.success("You're going! See you there.");
      setRsvpOpen(false);
      setRsvp({ children_count: 0, adults_count: 0, children: [], note: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not RSVP"));
    }
  };

  const cancelRsvp = async () => {
    try {
      await api.delete(`/events/${event.event_id}/rsvp`);
      toast.success("RSVP cancelled");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel"));
    }
  };

  const setChild = (i, key, val) => {
    const arr = [...rsvp.children];
    arr[i] = { ...arr[i], [key]: val };
    setRsvp({ ...rsvp, children: arr });
  };

  const sendContactMessage = async () => {
    const text = contactMessage.trim();
    if (text.length < 5) {
      toast.error("Please write a short message first");
      return;
    }
    setContactSending(true);
    try {
      await api.post(`/events/${event.event_id}/contact-organiser`, { message: text });
      toast.success(`Message sent to ${event.creator_name}`);
      setContactMessage("");
      setContactOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Could not send your message"));
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="event-detail-page">
      {event.image_url && (
        <div className="rounded-3xl overflow-hidden shadow-md mb-6 h-64 sm:h-80 bg-sage-light flex items-center justify-center" data-testid="event-photo">
          <img src={event.image_url} alt={event.title} className="w-full h-full object-contain" />
        </div>
      )}

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2.5 py-1 rounded-full">{event.category}</span>
          {event.kind === "meetup" && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-sage text-white px-2.5 py-1 rounded-full">Meet-up</span>
          )}
          {event.age_range && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full" data-testid="event-age-range">{event.age_range}</span>
          )}
        </div>

        <h1 className="text-3xl sm:text-4xl font-black text-forest" data-testid="event-title">{event.title}</h1>

        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-forest/70">
          <span className="flex items-center gap-2" data-testid="event-date"><CalendarDays className="w-4 h-4 text-sage-dark" /> {dateLabel}{event.start_time ? ` · ${event.start_time}` : ""}{event.end_time ? ` – ${event.end_time}` : ""}</span>
          <span className="flex items-center gap-2" data-testid="event-location"><MapPin className="w-4 h-4 text-sage-dark" /> {event.location}</span>
        </div>
        <p className="text-xs text-forest/40 mt-2">Hosted by {event.creator_name}</p>

        {event.description && <p className="text-forest/75 leading-relaxed mt-5 whitespace-pre-line" data-testid="event-description">{event.description}</p>}

        <div className="flex flex-wrap items-center gap-4 mt-6 pt-6 border-t border-sage/15">
          <div className="flex items-center gap-2 text-sm font-bold text-forest" data-testid="attendee-count">
            <Users className="w-5 h-5 text-terracotta-dark" />
            {event.attendee_count} {event.attendee_count === 1 ? "family" : "families"} going · {event.capacity == null ? "No limit" : `${event.spots_filled}/${event.capacity} spots filled`}
          </div>
          <div className="flex-1 max-w-xs">
            {event.capacity != null && <Progress value={Math.min(100, (event.spots_filled / event.capacity) * 100)} className="h-2 bg-sage-light" />}
          </div>
          <div className="ml-auto">
            {user?.role === "parent" && (
              mine ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold bg-sage-light text-sage-dark px-4 py-2 rounded-full flex items-center gap-1.5" data-testid="going-badge">
                    <CheckCircle2 className="w-4 h-4" /> You're going
                  </span>
                  <Button size="sm" variant="ghost" className="rounded-full text-terracotta-dark hover:bg-terracotta-light" onClick={cancelRsvp} data-testid="cancel-rsvp-btn">
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setRsvpOpen(true)} disabled={full} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md px-6" data-testid="im-going-btn">
                  {full ? "Event full" : "I'm Interested"}
                </Button>
              )
            )}
            {!user && (
              <Link to="/login"><Button className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white px-6" data-testid="login-to-rsvp-btn">Sign in to join</Button></Link>
            )}
          </div>
        </div>
      </div>

      {user && (
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm mb-6" data-testid="whos-coming-section">
          <h2 className="text-xl font-black text-forest mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-terracotta-dark" /> Who's Interested ({whosComing.length})
          </h2>
          {whosComing.length === 0 ? (
            <p className="text-sm text-forest/50" data-testid="whos-coming-empty">No one has RSVP'd yet — be the first!</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {whosComing.map((w) => (
                <div key={w.rsvp_id} className="bg-softstone/60 rounded-2xl p-4" data-testid={`whos-coming-${w.rsvp_id}`}>
                  <p className="font-heading font-bold text-forest">
                    {w.first_name}
                    <span className="text-xs font-body font-semibold text-forest/50"> · {w.children_count} child{w.children_count > 1 ? "ren" : ""}</span>
                  </p>
                  {w.kids_ages.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {w.kids_ages.map((a, i) => (
                        <span key={i} className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{a} yrs</span>
                      ))}
                    </div>
                  )}
                  {w.interests.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {w.interests.map((t) => (
                        <span key={t} className="text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm" data-testid="event-contact-section">
        <h2 className="text-xl font-black text-forest mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-sage-dark" /> Contact the organiser
        </h2>
        {event.creator_email ? (
          <>
            <p className="text-sm text-forest/70 mb-4" data-testid="contact-organiser-blurb">
              Have a question for <strong>{event.creator_name}</strong>? Send them a message right from here.
            </p>
            {!user ? (
              <p className="text-sm text-forest/60" data-testid="contact-organiser-login">
                <Link to="/login" className="font-bold text-terracotta-dark hover:underline">Sign in</Link> to send the organiser a message.
              </p>
            ) : !contactOpen ? (
              <Button
                onClick={() => setContactOpen(true)}
                className="rounded-full bg-sage hover:bg-sage-dark text-white shadow-md px-6"
                data-testid="contact-organiser-open-btn"
              >
                <Mail className="w-4 h-4 mr-2" /> Message {event.creator_name}
              </Button>
            ) : (
              <div className="space-y-3" data-testid="contact-organiser-form">
                <div>
                  <Label className="text-forest/80">Your message *</Label>
                  <Textarea
                    value={contactMessage}
                    onChange={(e) => setContactMessage(e.target.value)}
                    rows={5}
                    placeholder={`Hi ${event.creator_name}, I had a question about your event...`}
                    className="mt-1 rounded-xl bg-white border-sage/30"
                    data-testid="contact-organiser-message-input"
                  />
                  <p className="text-[11px] text-forest/50 mt-1">
                    We'll email {event.creator_name} using your Compass Kids account email so they can reply to you directly.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={sendContactMessage}
                    disabled={contactSending || contactMessage.trim().length < 5}
                    className="rounded-full bg-sage hover:bg-sage-dark text-white shadow-md"
                    data-testid="contact-organiser-send-btn"
                  >
                    <Send className="w-4 h-4 mr-2" /> {contactSending ? "Sending..." : "Send message"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setContactOpen(false); setContactMessage(""); }}
                    disabled={contactSending}
                    className="rounded-full text-forest/60 hover:bg-sage-light"
                    data-testid="contact-organiser-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-forest/60" data-testid="contact-organiser-empty">
            The organiser hasn't shared a contact email for this event yet.
          </p>
        )}
      </div>

      <Dialog open={rsvpOpen} onOpenChange={setRsvpOpen}>
        <DialogContent className="rounded-3xl bg-cream" data-testid="rsvp-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">I'm Going: {event.title}</DialogTitle>
            <DialogDescription className="sr-only">Register your children for this event.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-forest/60">
              Joining as <strong>{user?.name}</strong> · {event.capacity == null ? "no limit on spots" : `${event.capacity - event.spots_filled} spots left`}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-forest/80">Number of children *</Label>
                <Input type="number" min={0} max={10} value={rsvp.children_count}
                  onChange={(e) => {
                    const n = Math.max(0, Math.min(10, parseInt(e.target.value) || 0));
                    setRsvp((r) => {
                      const next = r.children.slice(0, n);
                      while (next.length < n) next.push({ gender: "", age: "" });
                      return { ...r, children_count: n, children: next };
                    });
                  }}
                  className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-children-count" />
              </div>
              <div>
                <Label className="text-forest/80">Number of adults *</Label>
                <Input type="number" min={0} max={10} value={rsvp.adults_count}
                  onChange={(e) => setRsvp({ ...rsvp, adults_count: Math.max(0, parseInt(e.target.value) || 0) })}
                  className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-adults-count" />
              </div>
            </div>
            {rsvp.children_count > 0 && (
              <div className="space-y-2" data-testid="child-details">
                <Label className="text-forest/80">Children details</Label>
                {rsvp.children.map((c, i) => (
                  <div key={i} className="grid grid-cols-[1fr_90px] gap-2" data-testid={`child-row-${i}`}>
                    <select
                      value={c.gender || ""}
                      onChange={(e) => setChild(i, "gender", e.target.value)}
                      className="rounded-xl bg-white border border-sage/30 px-3 py-2 text-sm text-forest focus:outline-none focus:ring-2 focus:ring-sage/40"
                      data-testid={`child-gender-${i}`}
                      aria-label={`Child ${i + 1} gender`}
                    >
                      <option value="">Gender</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="other">Other</option>
                    </select>
                    <Input
                      type="number"
                      min={0}
                      max={18}
                      value={c.age}
                      onChange={(e) => setChild(i, "age", e.target.value)}
                      placeholder="Age"
                      className="rounded-xl bg-white border-sage/30"
                      data-testid={`child-age-${i}`}
                    />
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label className="text-forest/80">Note for the organizer</Label>
              <Textarea value={rsvp.note} onChange={(e) => setRsvp({ ...rsvp, note: e.target.value })} rows={2}
                placeholder="Allergies, questions, anything to know..." className="mt-1 rounded-xl bg-white border-sage/30" data-testid="rsvp-note-input" />
            </div>
            <Button onClick={submitRsvp} className="w-full rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="rsvp-submit-btn">
              Confirm — I'm Going
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
